import { ComponentInternalInstance, getCurrentInstance, Ref, ref, toRef, reactive } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';
import { checkVirusStatus } from '@/renderer/scripts/utils/checkVirusStatus';
export interface ConfirmAuthParams {
  changed?: CoFile.TeamOrMemberItem[];
  deleted?: CoFile.TeamOrMemberItem[];
  added?: CoFile.TeamOrMemberItem[];
}

export interface AuthParams {
  path: string;
  userId?: number | string;
  virusAuditStatus?: number;
  spaceTag: string;
  sensitiveWordAuditStatus?: number;
  spaceOrgId?: number;
  teamId?: number;
}

export interface AuthOptions {
  limit?: number;
  keyword?: string;
}

type GetDirPermissionsParams = {
  spaceOrgId?: number;
  spaceTag?: string;
};

type Handler = {
  show: Ref<boolean>;
  authParams: { params: AuthParams; options?: AuthOptions };
  loading: Ref<boolean>;
  hasMore: Ref<boolean>;
  teamId: Ref<number | undefined>;
  dirPermisionList: Ref<CoFile.TeamOrMemberItem[]>;
  nextMarker: Ref<string | undefined>;
  methods: {
    canOpenAuthDir: (params: AuthParams) => boolean;
    initAuthDir: (params: AuthParams, options?: AuthOptions) => void;
    onSearchPermissions: (keyword: string) => void;
    getDirPermissions: (options?: { limit: number }) => Promise<void>;
    onConfirmAuth: (params: ConfirmAuthParams) => Promise<boolean>;
    reset: () => void;
  };
};
export default function handler(spaceId: Ref<SpaceId>): Handler {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;
  const show = ref(false);
  const dirPermissionParams: GetDirPermissionsParams = {
    spaceOrgId: 0,
    spaceTag: '',
  };
  const dirPermissionData: {
    loading: boolean;
    hasMore: boolean;
    list: CoFile.TeamOrMemberItem[];
    nextMarker?: string;
  } = reactive({
    loading: false,
    hasMore: false,
    list: [], // 保存初始目录的授权列表
    nextMarker: '',
  });
  let dirPath = '';
  const teamId = ref<number | undefined>(); // 当前授权弹窗是操作哪个企业空间（个人空间、协作空间文件无需处理）
  const authParams = reactive({
    params: {
      path: '',
      spaceTag: '',
    },
  });

  const methods = {
    reset() {
      dirPermissionData.list = [];
    },
    checkIfTeamAdmin(item: SMH.GetAuthorityDirectoryData) {
      return !!(teamId.value && item.user?.canManageTeams?.includes(teamId.value));
    },
    disableMemberOrTeam(
      // 这里的item.spaceTag指的是文件的spaceTag
      item: SMH.GetAuthorityDirectoryData & {
        spaceTag?: string;
      },
    ) {
      const ownerRoleId = store.getters.ownerRole?.id;
      const userId = (item.userId && +item.userId) || -1;
      const isTeamAdmin = methods.checkIfTeamAdmin(item);
      const isInPersonalSpace =
        item.spaceTag === 'personal' || vm.$route.name === 'personalSpace' || vm.$route.query?.spaceTag === 'personal';
      const disabledAdmin = !isInPersonalSpace && (checkRoleIsAdmin(item.user?.role as string, true) || isTeamAdmin);

      // 本身为继承成员，且已经为禁止访问成员，则不允许操作
      // 默认权限的成员也不允许操作
      // 管理员、超级管理员及当前空间的团队管理员不允许操作（除个人空间外）
      return (
        (item.tag === 1 && !!item.isForbidden) ||
        !!item.isDefault ||
        item.roleId === ownerRoleId ||
        userId === store.getters.userId ||
        disabledAdmin
      );
    },
    resetData() {
      dirPermissionData.list = [];
      dirPermissionData.nextMarker = '';
      dirPermissionData.hasMore = false;
    },
    // 是否可打开共享授权弹窗
    canOpenAuthDir(params: AuthParams) {
      if (params.virusAuditStatus || params.virusAuditStatus === 0) {
        const status = [
          {
            userId: Number(params.userId),
            virusAuditStatus: params.virusAuditStatus as number,
            sensitiveWordAuditStatus: params.sensitiveWordAuditStatus as number,
          },
        ];
        if (!checkVirusStatus(status, 'auth')) return false;
      }
      return true;
    },
    // 初始化加载共享授权数据
    async initAuthDir(params: AuthParams, options?: AuthOptions) {
      if (dirPermissionData.loading) return;
      // 每次打开时清空一下列表
      methods.resetData();
      dirPath = params.path;
      teamId.value = params?.teamId;
      dirPermissionParams.spaceOrgId = params?.spaceOrgId;
      dirPermissionParams.spaceTag = params.spaceTag;
      await methods.getDirPermissions({ limit: options?.limit, keyword: options?.keyword });
    },
    async getDirPermissions(options?: { limit?: number; keyword?: string }) {
      if (dirPermissionData.loading) return;
      if (dirPermissionData.list.length > 0 && !dirPermissionData.hasMore) return;
      const limit = options?.limit ?? 500;

      dirPermissionData.loading = true;
      try {
        const { data } = await smh.userAPI.getDirPermissions({
          dirPath,
          spaceId: spaceId.value,
          spaceOrgId: dirPermissionParams.spaceOrgId,
          limit,
          marker: dirPermissionData.nextMarker,
          keyword: options?.keyword,
        });
        const list = data.contents.map((item: any) => {
          const permission = {
            name: '',
            checked: false,
            roleId: -1,
            roleName: '',
            tag: 0,
            disabledOperate: false,
            isForbidden: false,
            isDefault: undefined,
          };
          if (item.user) {
            const { user } = item;
            Object.assign(permission, {
              name: user.nickname as string,
              roleId: item.roleId,
              inheritRoleId: item.tag === 1 && !item.inheritRoleId ? item.roleId : item.inheritRoleId,
              role: methods.checkIfTeamAdmin(item) ? 'teamAdmin' : item?.user?.role,
              orgId: item?.user?.orgId,
              roleName: item.name,
              tag: item.tag,
              userId: user.id as number,
              avatar: user.avatar,
              countryCode: user.countryCode,
              phoneNumber: user.phoneNumber,
              accountName: user.accountName,
              isAdmin:
                item?.user?.role === 'teamAdmin'
                  ? methods.checkIfTeamAdmin(item)
                  : checkRoleIsAdmin(item.user?.role, true),
            });
          }
          if (item.team) {
            const { team } = item;
            Object.assign(permission, {
              name: team.name,
              id: team.id as number,
              roleId: item.roleId,
              inheritRoleId: item.tag === 1 && !item.inheritRoleId ? item.roleId : item.inheritRoleId,
              roleName: item.name,
              tag: item.tag,
              userCount: team.userCount,
              spaceId: team.spaceId,
            });
          }
          permission.disabledOperate = methods.disableMemberOrTeam({
            ...item,
            spaceTag: dirPermissionParams.spaceTag,
          });
          permission.isDefault = (item as any)?.isDefault;
          permission.isForbidden = (item as any)?.isForbidden;
          return permission;
        });
        dirPermissionData.list = [...dirPermissionData.list, ...list];
        dirPermissionData.nextMarker = data.nextMarker;
        dirPermissionData.hasMore = !!data.nextMarker;
      } catch (error: any) {
        tips.error(error);
      }
      dirPermissionData.loading = false;
    },
    // 搜索共享授权列表
    onSearchPermissions(keyword: string) {
      methods.resetData();
      methods.getDirPermissions({ keyword });
    },
    /**
     * 删除团队成员,默认显示tips
     * @param userIds [xx,xx].join(',')
     * @param showTips 默认为true显示 若false不显示
     */
    async removeMember(userIds: number[], showTips = true, tid: number) {
      showTips && tips.loading(i18n_t('正在移除...'));
      await smh.userAPI.deleteTeamMember({
        teamId: tid,
        userIds,
      });
      showTips && tips.success(i18n_t('移除成功'));
    },
    // 空间 添加成员
    async addTeamMember(userIds: number[], authorizeTo: any[], tid: number) {
      // eslint-disable-next-line no-useless-catch
      try {
        await smh.userAPI.addTeamMember({
          teamId: tid,
          userIds,
          authorizeTo,
        });
      } catch (e) {
        throw e;
      }
    },
    // 空间 修改成员权限
    async editFileRole(authorizeTo: any[], tid: number) {
      let editSuccessful = false;
      try {
        tips.loading(i18n_t('正在修改...'));
        await smh.userAPI.editTeamMemberFileRole({
          teamId: tid,
          authorizeTo,
        });
        editSuccessful = true;
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return editSuccessful;
    },
    async onConfirmAuth(params: ConfirmAuthParams) {
      if (!dirPath) {
        tips.error(i18n_t('未选中文件夹'));
        return false;
      }
      if (dirPermissionData.loading) return false;
      const { changed = [], deleted = [], added = [] } = params;
      // 设置总体操作数不要超过1w
      const max = 10000;
      if (changed.length + added.length + deleted.length > max) {
        tips.error(i18n_t('一次性操作用户超过1万上限，请重新选择'));
        return false;
      }
      dirPermissionData.loading = true;
      tips.loading(i18n_t('处理中，请稍后...'), 0);
      // 更新成员
      try {
        const updated = [...(changed || []), ...(added || [])];
        if (updated && updated.length > 0) {
          await smh.commonAPI.postDirPermission({
            dirPath,
            authorizeTo: updated.map(i => {
              const tmp: SMH.DirAuthorize = {
                roleId: i.roleId as number,
                userId: i.userId,
                spaceId: i.spaceId,
                name: i.name,
              };
              return tmp;
            }),
            spaceId: spaceId.value,
          });
          const inheritChange = updated.filter(item => item.type === 'inherit');
          if (inheritChange.length > 0) {
            reporter.report('authorization', {
              change_authorization_inherit: inheritChange.length,
            });
          }
        }
      } catch (error: any) {
        if (error?.code === 'RoleNotFound') {
          // 如果是由于全局缓存角色列表未更新导致角色不存在，则重拉一遍
          store.dispatch('GetRoles');
        }
        const msg = [i18n_t('设置失败')];
        msg.push(formatError(error.code, true));
        tips.error(msg.filter(s => s).join(','));
        dirPermissionData.loading = false;
        return false;
      }
      // 删除成员
      try {
        if (deleted && deleted.length > 0) {
          await smh.commonAPI.deleteDirPermissions({
            dirPath,
            authorizeTo: deleted.map(i => {
              const tmp: SMH.DirAuthorize = {
                roleId: i.roleId as number,
                userId: i.userId,
                // 授权用户的 id
                spaceId: i.spaceId,
                // 授权团队的空间 id
                name: i.name,
              };
              return tmp;
            }),
            spaceId: spaceId.value,
          });
        }
        // 两个请求都成功后关闭弹窗
        tips.success(i18n_t('设置成功'));
        show.value = false;
      } catch (error: any) {
        const msg = [i18n_t('设置失败')];
        msg.push(formatError(error.code, true));
        tips.error(msg.filter(s => s).join(','));
        return false;
      }
      dirPermissionData.loading = false;
      return true;
    },
  };
  return {
    show,
    teamId,
    authParams,
    loading: toRef(dirPermissionData, 'loading'),
    dirPermisionList: toRef(dirPermissionData, 'list'),
    hasMore: toRef(dirPermissionData, 'hasMore'),
    nextMarker: toRef(dirPermissionData, 'nextMarker'),
    methods: {
      canOpenAuthDir: methods.canOpenAuthDir,
      initAuthDir: methods.initAuthDir,
      onSearchPermissions: methods.onSearchPermissions,
      onConfirmAuth: methods.onConfirmAuth,
      reset: methods.reset,
      getDirPermissions: methods.getDirPermissions,
    },
  };
}
